import { PublicClientApplication, LogLevel } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import isInGroup from '@/auth/authorization';

const scopes = [
    'openid',
    'profile',
    'user.read',
    'group.read.all',
    'directory.read.all'
];

export default class AzureAD {
    constructor(config) {
        // Mit MSAL v4: Kein v1 Cleanup mehr nötig - v4 ist komplett neu geschrieben
        // Nur einmalig bei erstem Start alte Reste entfernen
        const hasCleanedBefore = sessionStorage.getItem('msal.v4.cleanup.done');
        if (!hasCleanedBefore) {
            this.cleanupLegacyMsalCache();
            sessionStorage.setItem('msal.v4.cleanup.done', 'true');
        }

        // LÖSUNG: sessionStorage + Cookies funktioniert auch mit Tracking Prevention
        // sessionStorage wird von Tracking Prevention NICHT blockiert
        // storeAuthStateInCookie speichert kritische Auth-Daten in Cookies
        this.config = {
            auth: {
                clientId: config.clientId,
                authority: 'https://login.microsoftonline.com/dgzfp.onmicrosoft.com',
                redirectUri: config.redirectUri || window.location.origin,
                navigateToLoginRequestUrl: true
            },
            cache: {
                cacheLocation: 'sessionStorage', // Funktioniert mit Tracking Prevention!
                storeAuthStateInCookie: true, // Auth-State in Cookies (Fallback)
                secureCookies: false // WICHTIG: true nur bei HTTPS in Production
            },
            system: {
                allowNativeBroker: false,
                allowRedirectInIframe: false,
                loggerOptions: {
                    loggerCallback: this.loggerCallback,
                    logLevel: LogLevel.Info, // Detaillierte Logs auch in Production
                    piiLoggingEnabled: false // Keine persönlichen Daten loggen
                }
            }
        };

        this.msal = new PublicClientApplication(this.config);
        this.groupFilterPrefix = config.groupFilterPrefix || '';

        // MSAL v4: Initialisierung ist async - Promise speichern für index.js
        this.initializationPromise = this.initializeAuth();
    }

    /**
     * Löscht alte MSAL v1/v2 Cache-Einträge - nur beim ersten Start mit MSAL v4.
     * MSAL v4 verwendet ein neues Cache-Format.
     */
    cleanupLegacyMsalCache() {
        try {
            // MSAL v1 verwendete andere Schlüssel als MSAL v2
            const msalV1Keys = [
                'msal.idtoken',
                'msal.client.info',
                'msal.error',
                'msal.error.description',
                'msal.login.request',
                'msal.token.keys',
                'msal.token.renew.status',
                'msal.session.state',
                'msal.state.login',
                'msal.state.renew',
                'msal.username',
                'msal.login.error',
                'msal.urlHash',
                'msal.nonce.idtoken'
            ];

            // localStorage bereinigen (mit Fehlerbehandlung für Tracking Prevention)
            try {
                msalV1Keys.forEach(key => {
                    try {
                        if (localStorage.getItem(key)) {
                            console.log('[MSAL Cleanup] Entferne alten MSAL v1 Eintrag:', key);
                            localStorage.removeItem(key);
                        }
                    } catch (e) {
                        // Einzelner Key-Zugriff fehlgeschlagen - weiter
                    }
                });

                // Zusätzlich: alle Einträge, die mit "msal." beginnen (außer MSAL v2 Einträge)
                Object.keys(localStorage).forEach(key => {
                    // MSAL v2 verwendet clientId-basierte Keys mit UUID
                    // MSAL v1 verwendet Keys wie "msal.authority|{guid}" oder "msal.error"
                    if (key.startsWith('msal.')) {
                        const isV2Key = key.match(/^msal\.[a-f0-9-]{36}\./i); // UUID pattern
                        const isV2AccountKey = key.includes('.{'); // z.B. msal.token.keys.{clientId}

                        if (!isV2Key && !isV2AccountKey) {
                            try {
                                console.log('[MSAL Cleanup] Entferne verdächtigen MSAL v1 Eintrag:', key);
                                localStorage.removeItem(key);
                            } catch (e) {
                                // Zugriff blockiert - nicht kritisch
                            }
                        }
                    }
                });
            } catch (error) {
                console.log('[MSAL Cleanup] localStorage nicht verfügbar (Tracking Prevention) - überspringe');
            }

            // sessionStorage bereinigen
            msalV1Keys.forEach(key => {
                if (sessionStorage.getItem(key)) {
                    console.log('[MSAL Cleanup] Entferne alten MSAL v1 Eintrag aus sessionStorage:', key);
                    sessionStorage.removeItem(key);
                }
            });

            Object.keys(sessionStorage).forEach(key => {
                // MSAL v2 verwendet clientId-basierte Keys
                if (key.startsWith('msal.')) {
                    const isV2Key = key.match(/^msal\.[a-f0-9-]{36}\./i);
                    const isV2AccountKey = key.includes('.{');

                    if (!isV2Key && !isV2AccountKey) {
                        console.log('[MSAL Cleanup] Entferne verdächtigen MSAL v1 Eintrag aus sessionStorage:', key);
                        sessionStorage.removeItem(key);
                    }
                }
            });

            // MSAL v1 Cookies löschen
            const msalCookies = ['msal.idtoken', 'msal.state.login', 'msal.state.renew'];
            msalCookies.forEach(cookieName => {
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            });

            console.log('[MSAL Cleanup] Legacy MSAL Cache-Bereinigung abgeschlossen');
        } catch (error) {
            console.error('[MSAL Cleanup] Fehler beim Bereinigen des Legacy MSAL Cache:', error);
            // Fehler nicht werfen - App soll trotzdem starten
        }
    }

    // HINZUGEFÜGT: Graph Client Initialisierung
    initializeGraphClient() {
        try {
            const authProvider = {
                getAccessToken: async () => {
                    const accounts = this.msal.getAllAccounts();
                    if (accounts.length === 0) return null;

                    const response = await this.msal.acquireTokenSilent({
                        scopes: ['https://graph.microsoft.com/.default'],
                        account: accounts[0]
                    });

                    return response.accessToken;
                }
            };

            this.client = Client.initWithMiddleware({ authProvider });
        } catch (error) {
            console.error('Failed to initialize Graph client:', error);
        }
    }

    async initializeAuth() {
        try {
            // MSAL v4 BREAKING CHANGE: initialize() muss aufgerufen werden!
            await this.msal.initialize();
            console.log('[MSAL] v4 initialisiert und bereit');

            // Graph Client nach MSAL-Initialisierung erstellen
            this.initializeGraphClient();

            const currentUrl = window.location.href;
            const hasAuthCode = currentUrl.includes('code=');
            const hasAuthState = currentUrl.includes('state=');

            if (hasAuthCode && hasAuthState) {
                const response = await this.msal.handleRedirectPromise();
                if (response) {
                    this.handleLoginSuccess(response);
                }
            } else {
                await this.msal.handleRedirectPromise().catch(error => {
                    // Diese Fehler sind normal und können ignoriert werden
                    if (error.errorCode !== 'state_not_found' &&
                        error.errorCode !== 'hash_empty_error' &&
                        error.errorCode !== 'hash_does_not_contain_known_properties') {
                        console.error('[MSAL] Auth error:', error);
                    }
                });
            }
        } catch (error) {
            console.error('[MSAL] Auth initialization error:', error);
        }
    }

    loggerCallback(logLevel, message, piiLoggingEnabled) {
        // Alle MSAL Logs anzeigen (auch in Production)
        // Error = 0, Warning = 1, Info = 2, Verbose = 3, Trace = 4
        if (logLevel === LogLevel.Error) {
            console.error('[MSAL]', message);
        } else if (logLevel === LogLevel.Warning) {
            console.warn('[MSAL]', message);
        } else {
            console.log('[MSAL]', message);
        }
    }

    handleLoginSuccess(response) {
        // URL bereinigen
        if (window.location.search.includes('code=')) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
        }

        // KORRIGIERT: Sichere Router-Navigation
        if (window.app && window.app.$router && typeof window.app.$router.push === 'function') {
            const targetRoute = sessionStorage.getItem('targetRoute') || '/';
            sessionStorage.removeItem('targetRoute');

            try {
                const navigationPromise = window.app.$router.push(targetRoute);
                if (navigationPromise && typeof navigationPromise.catch === 'function') {
                    navigationPromise.catch(() => { }); // Ignoriere Navigation-Fehler
                }
            } catch (error) {
                // Navigation nicht verfügbar
            }
        }
    }

    async login(callback) {
        try {
            if (!window.location.pathname.includes('/login')) {
                sessionStorage.setItem('targetRoute', window.location.pathname);
            }

            // Bereinige eventuellen Auth-State vor neuem Login
            if (window.location.search.includes('code=') || window.location.hash) {
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
            }

            await this.msal.loginRedirect({
                scopes: scopes,
                prompt: 'select_account'
            });

            if (callback) callback();
        } catch (error) {
            console.error('Login initiation error:', error);
        }
    }

    clearAuthCache() {
        try {
            const accounts = this.msal.getAllAccounts();
            accounts.forEach(account => {
                this.msal.removeAccount(account);
            });

            // sessionStorage bereinigen
            try {
                sessionStorage.removeItem('taz.groups');
                sessionStorage.removeItem('taz.groups.exp');
                sessionStorage.removeItem('targetRoute');
            } catch (error) {
                // Nicht kritisch
            }
        } catch (error) {
            console.error('[MSAL] Error clearing auth cache:', error);
        }
    }

    logout(cb) {
        const accounts = this.msal.getAllAccounts();
        if (accounts.length > 0) {
            this.msal.logoutRedirect({
                account: accounts[0]
            });
        }
        if (cb) cb();
    }

    getUser() {
        const accounts = this.msal.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    isLoggedIn() {
        return this.msal.getAllAccounts().length > 0;
    }

    async getImage() {
        // Prüfen, ob User angemeldet ist
        if (!this.isLoggedIn()) {
            return ''; // Kein Bild, wenn nicht angemeldet
        }

        if (!this.client) {
            this.initializeGraphClient();
            if (!this.client) return '';
        }

        try {
            var req = await this.client
                .api('me/photo/$value')
                .responseType('blob')
                .get();

            return window.URL.createObjectURL(req);
        } catch (err) {
            // 401/403 sind normal wenn User kein Profilbild hat oder nicht angemeldet
            if (err.statusCode === 401 || err.statusCode === 403 || err.statusCode === 404) {
                return ''; // Kein Bild verfügbar
            }
            console.error('[MSAL] Error loading profile image:', err.message || err);
            return '';
        }
    }

    // KORRIGIERT: Transitive Gruppenmitgliedschaften
    async getUserGroups(filter) {
        if (!this.isLoggedIn()) return [];

        // Cache in sessionStorage (funktioniert mit Tracking Prevention)
        const cacheKey = 'taz.groups';
        const cacheExpKey = 'taz.groups.exp';

        try {
            const cached = sessionStorage.getItem(cacheKey);
            const cacheExp = sessionStorage.getItem(cacheExpKey);

            if (cached && cacheExp && new Date().getTime() < parseInt(cacheExp)) {
                return JSON.parse(cached);
            }
        } catch (error) {
            // Weiter ohne Cache
        }

        if (!this.client) {
            this.initializeGraphClient();
            if (!this.client) return [];
        }

        try {
            const userGroupsResp = await this.client
                .api('/me/transitiveMemberOf/microsoft.graph.group')
                .select('id,displayName,description')
                .get();

            const groupFilter = filter || this.groupFilterPrefix || 'taz-eq';
            const result = userGroupsResp.value.filter(group =>
                group.displayName &&
                group.displayName.toLowerCase().includes(groupFilter.toLowerCase())
            );

            // Cache für 5 Minuten in sessionStorage (funktioniert mit Tracking Prevention)
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify(result));
                sessionStorage.setItem(cacheExpKey, (new Date().getTime() + 5 * 60 * 1000).toString());
            } catch (error) {
                // Nicht kritisch - App funktioniert ohne Cache
            }

            return result;
        } catch (err) {
            console.error('[MSAL] Error loading groups:', err);

            // Bei 400/401 Fehler: Token ungültig, neu anmelden
            if (err.statusCode === 400 || err.statusCode === 401) {
                console.error('[MSAL] Token ungültig. Leite zur erneuten Anmeldung weiter...');
                this.clearAuthCache();
                await this.login();
            }

            return [];
        }
    }

    isInGroupSync(userGroups, requiredGroups) {
        if (!Array.isArray(userGroups) || userGroups.length === 0) {
            return false;
        }

        const requiredGroupsArray = Array.isArray(requiredGroups) ? requiredGroups : [requiredGroups];

        return requiredGroupsArray.some(requiredGroup =>
            userGroups.some(userGroup =>
                userGroup.displayName &&
                userGroup.displayName.toLowerCase().includes(requiredGroup.toLowerCase())
            )
        );
    }

    async isInGroupAsync(requiredGroups) {
        const groups = await this.getUserGroups();
        return this.isInGroupSync(groups, requiredGroups);
    }

    isLoginExpired() {
        const accounts = this.msal.getAllAccounts();
        if (accounts.length === 0) return true;

        try {
            const account = accounts[0];
            return false; // Vereinfachte Prüfung
        } catch (error) {
            return true;
        }
    }

    refreshToken(successCallback, errorCallback) {
        const accounts = this.msal.getAllAccounts();
        if (accounts.length === 0) {
            if (errorCallback) errorCallback();
            return;
        }

        this.msal.acquireTokenSilent({
            scopes: scopes,
            account: accounts[0]
        }).then(response => {
            if (successCallback) successCallback(response);
        }).catch(error => {
            if (error.name === 'InteractionRequiredAuthError') {
                this.login();
            } else if (errorCallback) {
                errorCallback(error);
            }
        });
    }
}
